<template>
  <Conference
    v-if="conference"
    :error="resolvedError"
    :conference="conference"
    :switching-room="isSwitchingRoom"
    @select-room="handleSelectRoom"
  />

  <AccessStateLayout v-else :description="statusDescription" :eyebrow="conferenceEyebrow" :title="statusTitle">
    <p class="rounded-2xl bg-slate-100 px-4 py-4 text-sm text-slate-700">
      {{ loadingMessage }}
    </p>

    <p
      v-if="displayErrorMessage"
      class="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700"
      role="alert"
    >
      {{ displayErrorMessage }}
    </p>
  </AccessStateLayout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useConvexMutation, useConvexQuery } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import { useConference } from '../convex/useConference';
import AccessStateLayout from './AccessStateLayout.vue';
import Conference from './Conference.vue';

const props = defineProps<{
  roomSlug: string;
}>();

const router = useRouter();

const conferenceSlug = computed(() => props.roomSlug);
const queryArgs = computed(() => ({ conferenceSlug: conferenceSlug.value }));
const { conference, isLoading, isSwitchingRoom, resolvedError, switchRoom } = useConference(conferenceSlug);
const { data: pendingSessionEndIntent } = useConvexQuery(api.sessionEndIntents.getPending, queryArgs);
const { mutate: consumeSessionEndIntent } = useConvexMutation(api.sessionEndIntents.consume);
const ROOM_ACCESS_DENIED_MARKER = 'is not permitted for this invite token.';
const handlingSessionEndIntent = ref(false);

watch(
  () => pendingSessionEndIntent.value?.id ?? null,
  async (intentId) => {
    if (!intentId || handlingSessionEndIntent.value) {
      return;
    }

    handlingSessionEndIntent.value = true;

    try {
      await consumeSessionEndIntent({
        conferenceSlug: conferenceSlug.value,
        intentId: intentId as never,
      });
    } catch {
      // Route away even if the intent was already consumed elsewhere.
    }

    await router.replace({ name: 'bye-bye' });
  },
);

watch(
  () => conference.value?.viewer.isKicked,
  async (isKicked) => {
    if (handlingSessionEndIntent.value || pendingSessionEndIntent.value?.id) {
      return;
    }

    if (isKicked) {
      await router.replace({
        name: 'kicked-conference',
        params: {
          roomSlug: conferenceSlug.value,
        },
      });
    }
  },
);

const conferenceEyebrow = computed(() => conference.value?.conference.title ?? 'Online Learning Session');
const isRoomAccessDenied = computed(() => resolvedError.value?.includes(ROOM_ACCESS_DENIED_MARKER) ?? false);
const statusTitle = computed(() => (isRoomAccessDenied.value ? 'This invite link cannot open this session' : 'Joining session'));
const statusDescription = computed(() => {
  if (isRoomAccessDenied.value) {
    return 'Your invite link is valid, but it does not include access to this session.';
  }

  if (resolvedError.value) {
    return 'Something is wrong with the link you are using to join the session. Please get a new one.';
  }

  if (conference.value) {
    return 'Your classroom is ready.';
  }

  if (isLoading.value) {
    return 'We are joining your session now.';
  }

  return 'We are preparing your session.';
});

const loadingMessage = computed(() => {
  if (isRoomAccessDenied.value) {
    return 'Open the session from the invite link you were given, or ask for a new link if you should have access to this room.';
  }

  return isLoading.value ? 'Loading the room list and participant roster for your session.' : 'Preparing your conference.';
});

const displayErrorMessage = computed(() => {
  if (!resolvedError.value) {
    return null;
  }

  if (isRoomAccessDenied.value) {
    return 'This room is not included in your invite token.';
  }

  return resolvedError.value;
});

async function handleSelectRoom(roomSlug: string): Promise<void> {
  await switchRoom(roomSlug);
}
</script>
